import deburr from 'lodash/deburr';
import { slugify, findNearestCoordinates, getDistance, formatDistance, hasDistance, getCoords, WithUserCoords, QueryString, parseHtml } from '@grandvisionhq/utils';
import { Location, useApiClient, Stores } from '@grandvisionhq/api-client';
import * as React from 'react';
import { useFeatureToggles, withApiConfig, withIntl, useIntl } from '@grandvisionhq/state';
import classnames from 'classnames';
import { Alert, Button, Dropdown, SkeletonLoader, MediaQuery, LoaderScreen, GoogleMaps, Breadcrumbs, List, Accordion, AccordionItem, IconLink } from '@grandvisionhq/common';
import { Form, Input, Util, FormControl } from '@grandvisionhq/forms';
import { createSelector } from 'reselect';
import { debounce } from 'throttle-debounce';
import { Heading } from '@grandvisionhq/elements';
import { Location as Location$1, OpeningHours, UseMyLocationButton, LocationList, LocationContactField } from '@grandvisionhq/locations';
import { Icon } from '@grandvisionhq/icons';
import { ServicesNotAvailable } from '@grandvisionhq/store-model';

const defaultSearchWhitelist = [
    'postalCode',
    'streetName',
    'town'
];
const normalize = (value)=>typeof value === 'string' || typeof value === 'number' ? deburr(value.toString()).toLowerCase() : ''
;
const searchStore = (store, query, searchWhitelist = defaultSearchWhitelist)=>{
    const normalizedQuery = normalize(query);
    return searchWhitelist.some((key)=>normalize(store[key]).includes(normalizedQuery)
    );
};

const normalizeTownName = (town = '')=>town.replace(/[\(\)\/]/g, '').replace(/\-/g, '_').replace(/\s/g, '-').toLowerCase()
;

const createBreadcrumbs = (store, rootLabel, storesRoute)=>{
    if (!storesRoute) {
        console.error('createBreadcrumbs: Could not create breadcrumbs because storesRoute was not found');
        return [];
    }
    return [
        {
            name: 'Home',
            url: '/'
        },
        {
            name: rootLabel,
            url: storesRoute
        },
        {
            name: store.town,
            url: `${storesRoute}?search=${store.town}`
        },
        {
            name: `${store.storeName}`,
            url: `${storesRoute}/${store.slug}`
        }, 
    ];
};
const getBookAppointmentUrl = (bookAppointmentUrl)=>({ code  })=>`${bookAppointmentUrl}?storeId=${code}`
;
const getStoreDetailUrl = (storesListUrl)=>(store)=>`${storesListUrl}/${slugify(store.town)}/${store.code}`
;
// checks that all services in array are being offered by the gives store
// services mean: the services being asked for
const areServicesAvailable = (store, services)=>{
    var ref;
    return !(store === null || store === void 0 ? void 0 : (ref = store.servicesNotAvailable) === null || ref === void 0 ? void 0 : ref.some((notAvailable)=>services.includes(notAvailable)
    ));
};

function asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$2(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$2(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
/**
 * Multi-country getCoordinates workaround. Use api-client's
 * Location.getCoordinates instead, unless location-service is configured with
 * a GOOGLE_MAPS_COUNTRY_RESTRICTION with multiple country codes.
 */ const getCoordinatesMultiCountry = function() {
    var _ref = _asyncToGenerator$2(function*(apiUrl, request) {
        var ref;
        const { places  } = yield Location.getPlacePredictionsExtended(apiUrl, {
            place: request.query
        });
        const place = places === null || places === void 0 ? void 0 : places[0];
        if (!place) {
            throw new Error('No matching place found');
        }
        const details = yield Location.getPlaceDetails(apiUrl, {
            id: place.id
        });
        return {
            address: ((ref = place.address) === null || ref === void 0 ? void 0 : ref.join(', ')) || '',
            coordinates: details.coordinates
        };
    });
    return function getCoordinatesMultiCountry(apiUrl, request) {
        return _ref.apply(this, arguments);
    };
}();

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  defaultSearchWhitelist: defaultSearchWhitelist,
  searchStore: searchStore,
  normalizeTownName: normalizeTownName,
  createBreadcrumbs: createBreadcrumbs,
  getBookAppointmentUrl: getBookAppointmentUrl,
  getStoreDetailUrl: getStoreDetailUrl,
  areServicesAvailable: areServicesAvailable,
  getCoordinatesMultiCountry: getCoordinatesMultiCountry
});

function _extends$1() {
    _extends$1 = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends$1.apply(this, arguments);
}
const withGetCoordinates = (Component)=>{
    const wrappedName = Component.displayName || Component.name || 'Component';
    const WithGetCoordinates = (props)=>{
        const { isMultiCountryLocationsEnabled  } = useFeatureToggles();
        const getCoordinates = isMultiCountryLocationsEnabled ? getCoordinatesMultiCountry : Location.getCoordinates;
        return(/*#__PURE__*/ React.createElement(Component, _extends$1({}, props, {
            getCoordinates: getCoordinates
        })));
    };
    WithGetCoordinates.displayName = `WithGetCoordinates(${wrappedName})`;
    return WithGetCoordinates;
};

const useGetCoordinates = ()=>{
    const { isMultiCountryLocationsEnabled  } = useFeatureToggles();
    return isMultiCountryLocationsEnabled ? getCoordinatesMultiCountry : Location.getCoordinates;
};

function asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator$1(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep$1(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _objectSpread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty$1(target, key, source[key]);
        });
    }
    return target;
}
function _objectWithoutProperties(source, excluded) {
    if (source == null) return {};
    var target = _objectWithoutPropertiesLoose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _objectWithoutPropertiesLoose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
const cityRegex = /^([a-zA-Z_\s\-]*)$/;
class PreferredStoreFinder extends React.Component {
    componentDidMount() {
        this.countries = this.getDistinctCountries();
    }
    static getDerivedStateFromProps({ selected , stores  }, prevState) {
        var ref;
        if (!prevState.search && selected && (selected === null || selected === void 0 ? void 0 : selected.code) !== ((ref = prevState.stores.selected) === null || ref === void 0 ? void 0 : ref.code)) {
            // Initialize state from props if user hasn't searched
            return _objectSpread$1({}, prevState, {
                isNoResults: false,
                location: selected,
                stores: {
                    selected,
                    list: findNearestCoordinates(selected, stores, {
                        limit: 10
                    })
                }
            });
        }
        return null;
    }
    render() {
        var ref;
        const { intl  } = this.props;
        const { search , stores , isNoResults , isTouched  } = this.state;
        const { list  } = stores;
        if (!this.props.stores.length) {
            return(/*#__PURE__*/ React.createElement(Alert, {
                type: "error",
                message: intl.getLabel('stores.preferredStore.errorMessage')
            }));
        }
        const isValid = this.isPostalCodeOrCity(search) && isTouched;
        const isInvalid = !this.isPostalCodeOrCity(search) && isTouched;
        const inputClasses = classnames('form-control__input', 'preferred-store-finder__input', {
            _is_valid: isValid,
            _is_invalid: isInvalid
        });
        return(/*#__PURE__*/ React.createElement("article", {
            className: "preferred-store-finder"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "preferred-store-finder__main"
        }, isNoResults && /*#__PURE__*/ React.createElement(Alert, {
            type: "info",
            message: intl.getLabel('stores.preferredStore.noResults')
        }), /*#__PURE__*/ React.createElement("div", {
            className: "preferred-store-finder__search"
        }, /*#__PURE__*/ React.createElement("label", {
            className: "form-control__label"
        }, intl.getLabel('stores.preferredStore.search.label'), /*#__PURE__*/ React.createElement("span", {
            className: "form-control__asterisk"
        }, "*")), /*#__PURE__*/ React.createElement(Form, {
            name: "preferred-store-finder",
            onSubmit: this.handleClickCTA,
            className: "preferred-store-finder__search-form"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "preferred-store-finder__search-form-content"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "preferred-store-finder__input-wrapper"
        }, /*#__PURE__*/ React.createElement(Input, {
            id: "search",
            "data-t": "search",
            className: inputClasses,
            placeholder: intl.getLabel('stores.form.findStore.fields.location.placeholder'),
            onChange: this.handleSearchChange,
            onBlur: this.handleBlur
        }), !this.isPostalCodeOrCity(search) && isTouched && /*#__PURE__*/ React.createElement("span", {
            className: "form-control__error"
        }, intl.getLabel('checkout.address.city.errorMessage'))), /*#__PURE__*/ React.createElement(Button, {
            className: "preferred-store-finder__button",
            color: "secondary",
            type: "submit",
            variant: !search || !this.isPostalCodeOrCity(search) ? 'outline' : 'solid',
            caption: intl.getLabel('stores.preferredStore.cta'),
            onClick: this.handleClickCTA
        })))), list.length > 0 && /*#__PURE__*/ React.createElement("div", {
            className: "preferred-store-finder__result"
        }, /*#__PURE__*/ React.createElement("label", {
            className: "form-control"
        }, /*#__PURE__*/ React.createElement("span", {
            className: "form-control__label"
        }, intl.getLabel('stores.preferredStore.result.label')), /*#__PURE__*/ React.createElement(Dropdown, {
            required: true,
            name: "result",
            handleChange: this.handleStoreSelect,
            options: this.getDropdownOptions(intl.distanceUnit),
            value: (stores === null || stores === void 0 ? void 0 : (ref = stores.selected) === null || ref === void 0 ? void 0 : ref.code) || ''
        }))))));
    }
    checkForPostalCode(search) {
        const postCodeRegex = Util.validations.getPostalCodeRegex(this.props.intl.country);
        return (postCodeRegex === null || postCodeRegex === void 0 ? void 0 : postCodeRegex.test(search)) ? `postcode ${search}` : search;
    }
    constructor(...args){
        super(...args);
        this.state = {
            search: '',
            isNoResults: false,
            stores: {
                list: []
            }
        };
        this.countries = [];
        this.getDropdownOptions = (distanceUnit)=>{
            const { location , stores  } = this.state;
            return stores.list.map((item)=>{
                const { town , streetName , streetNumber , code: value  } = item;
                const distance = getDistance(location || {}, item);
                let label = `${town} - ${streetName}`;
                if (streetNumber) {
                    label += ` ${streetNumber}`;
                }
                if (distance !== Infinity) {
                    label += ` (± ${formatDistance(distance, distanceUnit, {
                        precision: 0
                    })})`;
                }
                return {
                    label,
                    value
                };
            });
        };
        this.handleBlur = ()=>{
            if (!this.state.isTouched) {
                this.setState({
                    isTouched: true
                });
            }
        };
        this.handleSearchChange = ({ value: search  })=>this.setState({
                search,
                isNoResults: false
            })
        ;
        this.handleStoreSelect = ({ value  })=>{
            const { onStoreSelect  } = this.props;
            const { list  } = this.state.stores;
            const store = list.find((item)=>item.code === Number(value)
            );
            if (!store) {
                return;
            }
            onStoreSelect(store);
        };
        var _this = this;
        this.handleClickCTA = _asyncToGenerator$1(function*() {
            const { search  } = _this.state;
            if (!search || !_this.isPostalCodeOrCity(search)) {
                return;
            }
            const result = yield _this.getCoordinatesResult(_this.checkForPostalCode(search));
            if (!result) {
                return _this.setState({
                    isNoResults: true
                });
            }
            const l = result.coordinates;
            const location = {
                lat: l.lat,
                lon: l.lng
            };
            const list = findNearestCoordinates(location, _this.props.stores, {
                limit: 10
            });
            const selected = list[0];
            _this.setState({
                isNoResults: false,
                location,
                stores: {
                    selected,
                    list
                }
            }, ()=>_this.handleStoreSelect({
                    value: selected.code
                })
            );
        });
        this.getDistinctCountries = ()=>{
            const { stores  } = this.props;
            const result = [];
            stores.forEach(({ country  })=>!result.includes(country) && result.push(country)
            );
            return result;
        };
        var _this1 = this;
        this.getCoordinatesResult = function() {
            var _ref = _asyncToGenerator$1(function*(input) {
                const { apiConfig , getCoordinates  } = _this1.props;
                try {
                    return yield getCoordinates(apiConfig.locationsUrl, {
                        query: input
                    });
                } catch (e) {
                    console.error(e);
                }
            });
            return function(input) {
                return _ref.apply(this, arguments);
            };
        }();
        this.validateCity = (v)=>v.trim() ? cityRegex.test(v) : false
        ;
        this.isPostalCodeOrCity = (v)=>{
            return [
                Util.validations.getPostalCodeRegex(this.props.intl.country),
                this.validateCity
            ].some((validator)=>{
                return validator instanceof Function ? validator(v) : validator === null || validator === void 0 ? void 0 : validator.test(v);
            });
        };
    }
}
const PreferredStoreFinderLoader = ()=>/*#__PURE__*/ React.createElement(SkeletonLoader, null, /*#__PURE__*/ React.createElement("svg", {
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        height: "80px"
    }, /*#__PURE__*/ React.createElement("rect", {
        width: "80%",
        height: "32px",
        rx: "4",
        fill: "currentColor"
    }), /*#__PURE__*/ React.createElement("rect", {
        width: "50%",
        height: "32px",
        rx: "4",
        fill: "currentColor",
        y: "48"
    })))
;
const WithStores = (_param)=>{
    var { apiConfig  } = _param, rest = _objectWithoutProperties(_param, [
        "apiConfig"
    ]);
    const [isLoading, stores, error] = useApiClient(Stores.list, null, apiConfig.storesUrl);
    if (error) {
        throw error;
    }
    if (isLoading) {
        return(/*#__PURE__*/ React.createElement(PreferredStoreFinderLoader, null));
    }
    return(/*#__PURE__*/ React.createElement(PreferredStoreFinder, _extends({
        stores: stores || [],
        apiConfig: apiConfig
    }, rest)));
};
WithStores.displayName = 'WithStores(PreferredStoreFinder)';
var PreferredStoreFinder$1 = withGetCoordinates(withApiConfig(withIntl(WithStores)));

const Store = ({ store , onStoreAction , phoneHidden , emailHidden , targetPathAppointment , highlight , buttonText , bookAppointmentUrl , storeDetailsUrl , filterButtonService =[]  })=>{
    const { distanceUnit , getLabel  } = useIntl();
    const formattedDistance = hasDistance(store) ? formatDistance(store.distance, distanceUnit) : undefined;
    const primaryButton = areServicesAvailable(store, filterButtonService) && (onStoreAction ? /*#__PURE__*/ React.createElement(Button, {
        "data-t": `select-location-${store.code}`,
        caption: buttonText,
        onClick: ()=>onStoreAction(store)
    }) : /*#__PURE__*/ React.createElement(Button, {
        "data-t": `select-location-${store.code}`,
        caption: buttonText,
        href: bookAppointmentUrl || `${targetPathAppointment}?storeId=${store.code}`
    }));
    const secondaryButton = storeDetailsUrl && /*#__PURE__*/ React.createElement(Button, {
        variant: "outline",
        "data-t": `location-details-${store.code}`,
        href: storeDetailsUrl,
        caption: getLabel('stores.storeDetails.more')
    });
    return(/*#__PURE__*/ React.createElement(Location$1, {
        id: store.code,
        name: store.storeName,
        emailAddress: store.storeEmailAddress,
        phone: store.Phone1,
        phoneHidden: phoneHidden,
        emailHidden: emailHidden,
        highlight: highlight,
        locationDetailsUrl: storeDetailsUrl,
        formattedDistance: formattedDistance,
        openingHours: /*#__PURE__*/ React.createElement(OpeningHours, {
            schedule: store
        }),
        primaryButton: primaryButton,
        secondaryButton: secondaryButton,
        addressSize: "COMPLETE",
        addressData: {
            streetName: store.streetName,
            streetNumber: store.streetNumber,
            additionalStreetInfo: store.additionalStreetInfo,
            postalCode: store.postalCode,
            town: store.town
        }
    }));
};

var SearchType;
(function(SearchType) {
    SearchType["Query"] = 'query';
    SearchType["Location"] = 'location';
})(SearchType || (SearchType = {}));

const StoreSearchForm = ({ onQueryChange , onSubmit , initialValue ='' , placeholder ='' , searchType =SearchType.Query ,  })=>{
    const handleFormSubmit = (event)=>{
        event.preventDefault();
        if (searchType === SearchType.Location) {
            const query = event.target['store-search-query'].value;
            onSubmit && onSubmit(query);
        }
    };
    const handleChange = (event)=>{
        if (searchType === SearchType.Query) {
            onQueryChange && onQueryChange(event.value);
        }
    };
    return(/*#__PURE__*/ React.createElement("form", {
        className: "store-search-form",
        noValidate: true,
        onSubmit: handleFormSubmit
    }, /*#__PURE__*/ React.createElement(FormControl, {
        id: "store-search-query",
        type: "search",
        required: false,
        onChange: handleChange,
        initialState: {
            value: initialValue
        },
        placeholder: placeholder
    }), /*#__PURE__*/ React.createElement("button", {
        className: "store-search-form__submit",
        type: "submit"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "search"
    }))));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _asyncToGenerator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
const MAX_FILTERED_STORES = 15;
const DEFAULT_DEBOUNCE_MS = 250;
const getFilteredStores = createSelector((state, props)=>props.stores
, (state, props)=>props.searchWhitelist
, (state, props)=>props.searchType
, (state, props)=>props.storeFilter
, (state)=>state.search
, (state)=>state.userCoords
, (state)=>state.queryCoords
, (stores, searchWhitelist, searchType, storeFilter, search, userCoords, queryCoords)=>{
    // query based search:
    // returns stores that match the query in one of the whitelisted attributes
    if (searchType === SearchType.Query && search.length > 2) {
        stores = stores.filter((store)=>searchStore(store, search, searchWhitelist)
        );
    }
    // location based search:
    // if coords of location are known, sort stores by distance to the location
    if (searchType === SearchType.Location && search !== '') {
        if (queryCoords) {
            stores = findNearestCoordinates({
                lat: queryCoords.coordinates.lat,
                lon: queryCoords.coordinates.lng
            }, stores, {
                limit: MAX_FILTERED_STORES
            });
        } else if (!queryCoords) {
            stores = [];
        }
    }
    // if user coords are known, sort the stores by distance to the user
    if (userCoords) {
        stores = findNearestCoordinates({
            lat: userCoords.latitude,
            lon: userCoords.longitude
        }, stores, {
            limit: MAX_FILTERED_STORES
        });
    }
    if (storeFilter) {
        stores = stores.filter(storeFilter);
    }
    return stores;
});
const getMarkers = createSelector(getFilteredStores, (state)=>state.userCoords
, (state)=>state.queryCoords
, (stores, userCoords, queryCoords)=>{
    const markers = stores.map((store)=>({
            position: {
                lat: store.lat || 0,
                lng: store.lon || 0
            },
            code: store.code,
            isUserLocation: false
        })
    );
    // If we have the coords of the user, add a marker to show their location
    if (userCoords) {
        markers.push({
            position: {
                lat: userCoords.latitude,
                lng: userCoords.longitude
            },
            isUserLocation: true
        });
    } else if (queryCoords) {
        markers.push({
            position: queryCoords.coordinates,
            isUserLocation: true
        });
    }
    return markers;
});
const getQueryParams = ()=>{
    if (typeof window === 'undefined' || !window.location) {
        return {};
    }
    const loc = window.location;
    return QueryString.parse(loc.search.substr(1, loc.search.length));
};
class StoreFinder extends (React.PureComponent) {
    componentDidMount() {
        // When location based search is enabled, submit queryParams on init
        if (this.props.searchType === SearchType.Location && this.state.search !== '') {
            this.submitSearchQuery(this.state.search);
        }
        if (getQueryParams().userLocation === 'true') {
            getCoords((coords)=>this.handleUserCoords(coords)
            , (errorMessage)=>this.handleLocationError(errorMessage)
            , {
                yourLocationPermission: this.props.intl.getLabel('stores.yourLocation.permissionDenied'),
                yourLocationError: this.props.intl.getLabel('stores.yourLocation.error')
            });
        }
    }
    render() {
        const { highlightStore , userCoords , search , isLoading  } = this.state;
        const { intl , storeAction , initialValue , phoneHidden , emailHidden , showSearchBar , showCityName , showUseMyLocation , buttonText , getBookAppointmentUrl , getStoreDetailUrl , currentBreakpoint , searchType , mapOptions ={} , filterButtonService ,  } = this.props;
        const labels = {
            title: showCityName ? intl.getLabel('stores.form.findStore.titleWithCity') + ` ${initialValue}` : intl.getLabel('stores.form.findStore.title'),
            selectStore: intl.getLabel('stores.actions.selectStore'),
            placeholder: intl.getLabel('stores.form.findStore.fields.location.placeholder'),
            noResult: intl.getLabel('stores.form.findStore.errors.noResult'),
            useMyLocation: intl.getLabel('stores.actions.useMyLocation'),
            yourLocation: intl.getLabel('stores.yourLocation'),
            yourLocationHint: intl.getLabel('stores.yourLocation.hint'),
            yourLocationPermission: intl.getLabel('stores.yourLocation.permissionDenied'),
            yourLocationError: intl.getLabel('stores.yourLocation.error')
        };
        const stores = getFilteredStores(this.state, this.props);
        const markers = getMarkers(this.state, this.props);
        return(/*#__PURE__*/ React.createElement("article", {
            className: "store-finder"
        }, /*#__PURE__*/ React.createElement("div", {
            className: "store-finder__main",
            ref: this.mainRef
        }, showUseMyLocation && !isLoading && stores.length === 0 && !userCoords && /*#__PURE__*/ React.createElement(Alert, {
            className: "store-finder__location-alert",
            message: labels.yourLocationHint,
            icon: "info"
        }), /*#__PURE__*/ React.createElement("header", {
            className: "store-finder__header"
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            component: "h1",
            className: "store-finder__title"
        }, labels.title), showSearchBar && /*#__PURE__*/ React.createElement(StoreSearchForm, {
            onQueryChange: this.updateSearchQuery,
            onSubmit: this.submitSearchQuery,
            initialValue: search,
            placeholder: userCoords ? labels.yourLocation : labels.placeholder,
            searchType: searchType
        }), showUseMyLocation && !userCoords && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(WithUserCoords, {
            labels: labels,
            onUserCoords: this.handleUserCoords,
            onError: this.handleLocationError
        }, ({ hasBrowserSupport , getUserCoords  })=>hasBrowserSupport ? /*#__PURE__*/ React.createElement(UseMyLocationButton, {
                onClick: getUserCoords,
                caption: labels.useMyLocation
            }) : null
        ), this.state.errorMessage && /*#__PURE__*/ React.createElement(Alert, {
            type: "warning",
            icon: "warning",
            message: this.state.errorMessage,
            className: "store-finder__location-alert"
        }))), isLoading && stores.length === 0 && /*#__PURE__*/ React.createElement(LoaderScreen, {
            height: 50
        }), stores.length > 0 && /*#__PURE__*/ React.createElement(LocationList, {
            scrollToTargetId: highlightStore,
            scrollContainer: this.mainRef.current
        }, stores.map((store)=>/*#__PURE__*/ React.createElement(Store, {
                key: store.code,
                store: store,
                onStoreAction: storeAction,
                highlight: highlightStore === store.code,
                buttonText: buttonText || labels.selectStore,
                phoneHidden: phoneHidden,
                emailHidden: emailHidden,
                bookAppointmentUrl: getBookAppointmentUrl && getBookAppointmentUrl(store),
                storeDetailsUrl: getStoreDetailUrl && getStoreDetailUrl(store),
                filterButtonService: filterButtonService
            })
        )), !isLoading && stores.length === 0 && search.length !== 0 && /*#__PURE__*/ React.createElement("div", {
            className: "store-finder__message"
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "smallest",
            component: "span"
        }, `${labels.noResult}`, " ", search ? `'${search}'` : ''))), /*#__PURE__*/ React.createElement("aside", {
            className: "store-finder__map"
        }, currentBreakpoint !== MediaQuery.BreakpointName.PHONE && currentBreakpoint !== MediaQuery.BreakpointName.TABLET_PORTRAIT && /*#__PURE__*/ React.createElement(GoogleMaps, {
            markers: markers,
            highlight: highlightStore,
            onMarkerClick: this.onUpdateSelectedStore,
            mapOptions: mapOptions
        }))));
    }
    constructor(...args){
        super(...args);
        this.mainRef = /*#__PURE__*/ React.createRef();
        this.state = {
            highlightStore: null,
            search: this.props.initialValue || '',
            isLoading: true
        };
        this.clear = ()=>{
            this.setState({
                search: ''
            });
            const params = getQueryParams();
            params.search && delete params.search;
            const url = Object.keys(params).length > 0 ? `${window.location.pathname}${QueryString.stringify(params)}` : window.location.pathname;
            window.history.replaceState(null, 'store-finder', url);
        };
        this.setErrorState = (search)=>{
            this.setState({
                search,
                queryCoords: undefined,
                userCoords: undefined,
                highlightStore: null,
                isLoading: false
            }, this.updateHistoryState);
        };
        this.updateSearchQuery = debounce(this.props.debounceMs || DEFAULT_DEBOUNCE_MS, (search)=>{
            this.setState({
                search,
                highlightStore: null,
                isLoading: false
            }, this.updateHistoryState);
        });
        var _this = this;
        this.submitSearchQuery = function() {
            var _ref = _asyncToGenerator(function*(search) {
                const { locationsUrl  } = _this.props.apiConfig;
                if (!locationsUrl) {
                    console.error('submitSearchQuery: No apiUrl found for coordinates request');
                    return;
                }
                try {
                    const result = yield _this.props.getCoordinates(locationsUrl, {
                        query: search
                    });
                    if (result) {
                        _this.setState({
                            search,
                            queryCoords: result,
                            userCoords: undefined,
                            highlightStore: null,
                            isLoading: false
                        }, _this.updateHistoryState);
                    } else {
                        console.error(`Failed to fetch coordinates for stores. Result: ${result}`);
                        _this.setErrorState(search);
                    }
                } catch (err) {
                    console.error(`Failed to fetch coordinates for stores. Error: ${err}`);
                    _this.setErrorState(search);
                }
            });
            return function(search) {
                return _ref.apply(this, arguments);
            };
        }();
        this.onUpdateSelectedStore = (highlightStore)=>{
            if (this.props.updateSelectedStore) {
                this.props.updateSelectedStore(highlightStore);
            } else {
                this.setState({
                    highlightStore
                });
            }
        };
        this.clearQueryCoords = ()=>{
            this.setState({
                queryCoords: undefined
            });
        };
        this.updateHistoryState = ()=>{
            const params = _objectSpread({}, getQueryParams(), this.state.search !== '' ? {
                search: this.state.search
            } : {});
            const url = Object.keys(params).length > 0 ? `${window.location.pathname}${QueryString.stringify(params)}` : window.location.pathname;
            // clear queryCoords on empty searchquery to reset storelist
            if (this.state.queryCoords && !this.state.search) {
                this.clearQueryCoords();
                window.history.replaceState(null, 'store-finder', window.location.pathname);
            } else {
                window.history.replaceState(null, 'store-finder', url);
            }
        };
        this.handleUserCoords = (userCoords)=>{
            this.clear();
            this.setState({
                userCoords,
                queryCoords: undefined,
                isLoading: false
            });
        };
        this.handleLocationError = (errorMessage)=>{
            this.setState({
                errorMessage,
                isLoading: false
            });
        };
    }
}
StoreFinder.defaultProps = {
    phoneHidden: false,
    emailHidden: false,
    showSearchBar: true,
    showCityName: false,
    searchWhitelist: defaultSearchWhitelist,
    debounceMs: DEFAULT_DEBOUNCE_MS,
    searchType: SearchType.Query
};
var StoreFinder$1 = withGetCoordinates(withApiConfig(withIntl(MediaQuery.WithBreakpoints(StoreFinder))));

const StoreAwards = ({ awards , className , title  })=>{
    if (!awards || !awards.length || !awards[0].image) {
        return null;
    }
    return(/*#__PURE__*/ React.createElement("div", {
        className: classnames('store-awards', className)
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, title), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__award-images"
    }, awards.map(({ name , image  }, key)=>/*#__PURE__*/ React.createElement("img", {
            className: "store-detail__award",
            key: `award-${key}`,
            src: image,
            alt: name
        })
    ))));
};

const defaultAddressFormatter = (store)=>{
    return(/*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", null, store.streetName, store.streetNumber && ` ${store.streetNumber}`, store.additionalStreetInfo && ` ${store.additionalStreetInfo}`), /*#__PURE__*/ React.createElement("div", null, store.postalCode, " ", store.town)));
};

const StoreDetail = ({ store , intl , awards , breadcrumbs , findStoreUrl , sendMessageUrl , getBookAppointmentUrl , openingHours: { highlightCurrentDay =true  } = {} , addressFormatter , className , hideEmail =false , hidePhone =false ,  })=>{
    const storeOwnerName = `${store.ownerFirstName} ${store.ownerLastName}`;
    const validStore = store && (store.storeName || store.streetName);
    const position = React.useMemo(()=>({
            lat: store && store.lat || 0,
            lng: store && store.lon || 0
        })
    , [
        store
    ]);
    const markers = React.useMemo(()=>validStore ? [
            {
                position,
                code: 0
            }
        ] : []
    , [
        store, 
    ]);
    if (!validStore) {
        return(/*#__PURE__*/ React.createElement("div", {
            className: "store-detail__error"
        }, /*#__PURE__*/ React.createElement(Heading, null, intl.getLabel('stores.storeDetailsError.title')), /*#__PURE__*/ React.createElement("p", null, intl.getLabel('stores.storeDetailsError.message')), /*#__PURE__*/ React.createElement(Button, {
            caption: intl.getLabel('stores.form.findStore.title'),
            href: findStoreUrl || '/'
        })));
    }
    return(/*#__PURE__*/ React.createElement("article", {
        className: classnames('store-detail', className)
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__main"
    }, store && store.storeDetailImage && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__image",
        role: "presentation",
        style: {
            backgroundImage: `url(${store.storeDetailImage})`
        }
    }), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__content"
    }, breadcrumbs && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__breadcrumbs"
    }, /*#__PURE__*/ React.createElement(Breadcrumbs, {
        pages: breadcrumbs
    })), /*#__PURE__*/ React.createElement(Heading, {
        className: "store-detail__title"
    }, store.storeName), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__address",
        "data-t": "store-detail-address"
    }, addressFormatter ? addressFormatter(store) : defaultAddressFormatter(store)), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__contact"
    }, !hidePhone && store.Phone1 && /*#__PURE__*/ React.createElement(LocationContactField, {
        className: "store-detail__contact-field",
        fieldType: "phone",
        value: store.Phone1
    }), !hideEmail && store.storeEmailAddress && /*#__PURE__*/ React.createElement(LocationContactField, {
        className: "store-detail__contact-field",
        fieldType: "email",
        value: store.storeEmailAddress
    })), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__buttons"
    }, areServicesAvailable(store, [
        ServicesNotAvailable.APPOINTMENTS
    ]) && getBookAppointmentUrl && /*#__PURE__*/ React.createElement(Button, {
        className: "store-detail__button",
        "data-t": `store-link-${store.code}`,
        caption: intl.getLabel('stores.actions.selectStore'),
        href: getBookAppointmentUrl(store)
    }), /*#__PURE__*/ React.createElement(Button, {
        variant: "outline",
        className: "store-detail__button",
        caption: intl.getLabel('stores.actions.getDirections'),
        href: `https://www.google.com/maps/search/?api=1&query=${store.lat},${store.lon}`,
        hrefTarget: "_blank"
    })), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__columns"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__opening-hours store-detail__block"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, intl.getLabel('stores.openingTimes.title')), /*#__PURE__*/ React.createElement(OpeningHours, {
        schedule: store,
        highlightCurrentDay: highlightCurrentDay,
        isExpanded: true
    }), store.storenote && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__remarks"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, intl.getLabel('stores.storeNote.title'), ":"), store.storenote)), store.storeFacilities && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__services store-detail__block"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, intl.getLabel('stores.services.title')), /*#__PURE__*/ React.createElement(List, {
        items: store.storeFacilities,
        icon: "checkmark"
    }))), (store.ownerLastName || awards && awards.length > 0) && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__columns"
    }, store.ownerFirstName && store.ownerLastName && store.storeOwnerImage && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__store-manager store-detail__block"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__manager"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, intl.getLabel('stores.manager.title')), store.storeOwnerImage && /*#__PURE__*/ React.createElement("img", {
        className: "store-detail__image",
        src: store.storeOwnerImage,
        alt: storeOwnerName
    }), /*#__PURE__*/ React.createElement("div", null, storeOwnerName))), awards && awards.length > 0 && /*#__PURE__*/ React.createElement(StoreAwards, {
        className: "store-detail__block",
        awards: awards,
        title: intl.getLabel('stores.awards.title')
    })), store.storeDescription && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__description"
    }, /*#__PURE__*/ React.createElement(Accordion, null, /*#__PURE__*/ React.createElement(AccordionItem, {
        id: "store-detail-description",
        label: intl.getLabel('stores.storeDetails.title')
    }, parseHtml(store.storeDescription)))), sendMessageUrl && /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__customer-service"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "medium",
        className: "store-detail__heading"
    }, intl.getLabel('stores.customerService.title')), /*#__PURE__*/ React.createElement(IconLink, {
        className: "",
        href: sendMessageUrl || '/',
        iconName: "mail"
    }, intl.getLabel('stores.customerService.message'))))), /*#__PURE__*/ React.createElement("div", {
        className: "store-detail__map"
    }, /*#__PURE__*/ React.createElement(GoogleMaps, {
        markers: markers,
        center: position,
        highlight: 0
    }))));
};
var StoreDetail$1 = withIntl(StoreDetail);

export { PreferredStoreFinder$1 as PreferredStoreFinder, SearchType, Store, StoreDetail$1 as StoreDetail, StoreFinder$1 as StoreFinder, StoreSearchForm, index as Util, useGetCoordinates, withGetCoordinates };
//# sourceMappingURL=index.js.map
